import React, { useRef } from 'react';
import { Camera, FileText, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';

const isPdf = (f) => f && (f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));

const Preview = ({ file, onClear, disabled, testId }) => (
  <div className="border border-slate-200 rounded-lg p-2 flex items-center gap-3 bg-slate-50" data-testid={`${testId}-preview`}>
    {isPdf(file) ? (
      <div className="w-14 h-14 rounded-md bg-rose-100 border border-rose-200 flex flex-col items-center justify-center text-rose-700">
        <FileText className="w-6 h-6" />
        <span className="text-[9px] font-bold">PDF</span>
      </div>
    ) : (
      <img src={URL.createObjectURL(file)} alt="Box score preview" className="w-14 h-14 rounded-md object-cover bg-white border border-slate-200" />
    )}
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium text-slate-700 truncate">{file.name}</div>
      <div className="text-xs text-slate-400">{(file.size / 1024).toFixed(0)} KB</div>
    </div>
    <Button variant="ghost" size="sm" onClick={onClear} disabled={disabled} className="text-slate-400 hover:text-rose-600" aria-label="Remove file" data-testid={`${testId}-clear`}>
      <Trash2 className="w-4 h-4" />
    </Button>
  </div>
);

// One team's sheet: upload a photo/PDF, or open the phone camera directly.
export const ScanSlot = ({ label, file, onChange, disabled, testId }) => {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const pick = (e) => { onChange(e.target.files?.[0] || null); e.target.value = ''; };
  const btn = 'flex-1 flex flex-col items-center justify-center gap-1 border-2 border-dashed border-slate-300 rounded-lg py-3 text-xs text-slate-500 hover:border-[#00247D] hover:text-[#00247D] hover:bg-blue-50/30 transition-colors disabled:opacity-50';
  return (
    <div>
      <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">{label}</p>
      <input ref={fileRef} type="file" accept="image/*,application/pdf,.pdf" className="hidden" onChange={pick} data-testid={`${testId}-file-input`} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid={`${testId}-camera-input`} />
      {file ? (
        <Preview file={file} onClear={() => onChange(null)} disabled={disabled} testId={testId} />
      ) : (
        <div className="flex gap-2">
          <button type="button" className={btn} onClick={() => cameraRef.current?.click()} disabled={disabled} data-testid={`${testId}-camera-btn`}>
            <Camera className="w-5 h-5" />Take photo
          </button>
          <button type="button" className={btn} onClick={() => fileRef.current?.click()} disabled={disabled} data-testid={`${testId}-upload-btn`}>
            <Upload className="w-5 h-5" />Upload photo or PDF
          </button>
        </div>
      )}
    </div>
  );
};
